import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MarkAllAsReadButtonProps {
  onClick: () => Promise<boolean>;
  unreadCount: number;
  isLoading: boolean;
  section: 'upcoming' | 'history';
}

export function MarkAllAsReadButton({ 
  onClick, 
  unreadCount, 
  isLoading,
  section
}: MarkAllAsReadButtonProps) {
  const { toast } = useToast();
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [displayedCount, setDisplayedCount] = useState(unreadCount);

  // Update displayed count when the real count changes
  useEffect(() => {
    setDisplayedCount(unreadCount);
    
    // Reset success state when unread count increases
    if (unreadCount > 0 && isSuccessful) {
      setIsSuccessful(false);
    }
  }, [unreadCount, isSuccessful]);

  const handleClick = async () => {
    console.log(`MarkAllAsReadButton: Clicked for ${section} with ${unreadCount} unread`);
    
    try {
      // Set local displayed count to 0 immediately for better UI responsiveness
      setDisplayedCount(0);
      
      const success = await onClick();
      console.log(`MarkAllAsReadButton: Mark all as read result: ${success}`);
      
      if (!success && retryCount < 2) {
        // If the operation failed, automatically retry once
        setIsRetrying(true);
        setRetryCount(prev => prev + 1);
        
        toast({
          title: "Retrying...",
          description: "Trying again to mark messages as read",
          variant: "default",
        });
        
        // Wait a moment before retrying
        setTimeout(async () => {
          console.log("MarkAllAsReadButton: Retrying mark all as read");
          const retrySuccess = await onClick();
          console.log(`MarkAllAsReadButton: Retry result: ${retrySuccess}`);
          setIsSuccessful(retrySuccess);
          setIsRetrying(false);
        }, 2000);
      } else {
        setIsSuccessful(success);
        if (!success && retryCount >= 2) {
          toast({
            title: "Could not mark all as read",
            description: "Please try refreshing the page and try again",
            variant: "destructive",
          });
          // Reset displayed count to actual count since operation failed
          setDisplayedCount(unreadCount);
        }
      }
    } catch (error) {
      console.error("Error in MarkAllAsReadButton click handler:", error);
      setIsSuccessful(false);
      toast({
        title: "Error",
        description: "Failed to mark messages as read. Please try again.",
        variant: "destructive",
      });
      // Reset displayed count to actual count since operation failed
      setDisplayedCount(unreadCount);
    }
  };

  // If there are no unread messages, show "All read" button
  if (displayedCount === 0 || unreadCount === 0) {
    return (
      <Button variant="ghost" size="sm" disabled className="text-gray-400">
        <Check className="h-4 w-4 sm:mr-1" />
        <span className="hidden sm:inline">All read</span>
      </Button>
    );
  }

  // If marking was successful, show "Marked as read"
  if (isSuccessful) {
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled
        className="text-green-600"
      >
        <Check className="h-4 w-4 sm:mr-1" />
        <span className="hidden sm:inline">Marked as read</span>
      </Button>
    );
  }

  // Otherwise show the regular button
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={isLoading || isRetrying || displayedCount === 0}
      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
    >
      {isLoading || isRetrying ? (
        <>
          <Loader2 className="h-4 w-4 sm:mr-1 animate-spin" />
          <span className="hidden sm:inline">{isRetrying ? 'Retrying...' : 'Processing...'}</span>
        </>
      ) : (
        <>
          <Check className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">Mark all as read ({displayedCount})</span>
        </>
      )}
    </Button>
  );
}
